import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Mail, MapPin, ChevronRight } from 'lucide-react';
import { getStageColorClasses } from '../../../lib/stageColors';
import CallButton from '../../../components/clicktocall/CallButton';

// Shared address formatter — mirrors ClientRow.formatAddress
function formatAddress(address) {
  if (!address) return null;
  if (typeof address === 'string') return address || null;
  if (typeof address === 'object') {
    const { streetNumber, streetName, street, city, state, zip } = address;
    const streetPart = street || `${streetNumber || ''} ${streetName || ''}`.trim();
    const parts = [streetPart, city, state, zip].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  }
  return null;
}

/**
 * ClientCardMobile — single client card for the mobile list view.
 * Entire card taps to the client detail page.
 * Phone + email are independent tap-to-call/email anchors.
 */
const ClientCardMobile = ({ client }) => {
  const navigate = useNavigate();
  const address = formatAddress(client.address);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/clients/${client.id}`)}
      onKeyDown={(e) => e.key === 'Enter' && navigate(`/clients/${client.id}`)}
      className="flex items-start gap-3 px-4 py-4 hover:bg-gray-50 active:bg-gray-100 transition-colors cursor-pointer min-h-[72px]"
      data-testid={`client-card-${client.id}`}
    >
      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Row 1: name + stage badge */}
        <div className="flex items-center justify-between gap-2">
          <span className="font-bold text-base text-gray-900 truncate leading-snug">
            {client.name || 'Unnamed'}
          </span>
          {client.stage && (
            <span
              className={`text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${getStageColorClasses(client.stage)}`}
            >
              {client.stage}
            </span>
          )}
        </div>

        {/* Row 2: phone (tap-to-call via shared CallButton) */}
        {client.phone && (
          <div
            // Stop propagation so tapping the phone doesn't also navigate
            // the parent card into the client detail page.
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 mt-1.5 min-h-[36px] w-fit"
          >
            <CallButton
              phone={client.phone}
              surface="client-card-mobile"
              className="flex items-center gap-1.5 text-sm text-green-700 font-medium active:opacity-60"
              title="Call client"
              data-testid={`client-phone-${client.id}`}
            >
              <Phone className="h-3.5 w-3.5 flex-shrink-0" />
              {client.phone}
            </CallButton>
          </div>
        )}

        {/* Row 3: email (tap-to-email) */}
        {client.email && (
          <a
            href={`mailto:${client.email}`}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 mt-0.5 text-sm text-gray-500 min-h-[32px] w-fit active:opacity-60 max-w-full"
            data-testid={`client-email-${client.id}`}
          >
            <Mail className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">{client.email}</span>
          </a>
        )}

        {/* Row 4: address */}
        {address && (
          <div className="flex items-center gap-1.5 mt-0.5 text-xs text-gray-400">
            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">{address}</span>
          </div>
        )}
      </div>

      {/* Chevron — tap cue */}
      <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0 mt-1" />
    </div>
  );
};

export default ClientCardMobile;
